import {
  FormEvent,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Container, Grid } from '@material-ui/core';
import { BrandProvider, useBrandContext } from '../../contexts/brand';

import {
  BrandAssets,
  BrandBasicDetails,
  BrandCompanyContactBlock,
  BrandDetailTopBlock,
  BrandEditModal,
  BrandIdentityStatus,
  BrandNotFound,
  BrandRegisteredCampaigns,
  BrandSuspendedStatusBlock,
  BrandVettingDetailsBlock,
  BrandVettingModalV2,
  ConfirmationDialog,
  PoliticalVettingModal,
  VettingStatusModal,
  RcsVetSuccessModal,
  BrandEvents,
} from '../../components';
import { BrandVettingDetailsBlockHandle } from '../../components/BrandVettingDetailsBlock';
import {
  BrandDetail,
  BrandFeedback,
  BrandStatus,
  EditModal,
  Mno,
  MNOSuspendState,
  MnoSuspensionStatus,
  ModalType as StatusModal,
  ModalType as VettingModal,
  VettingAppeal,
  VettingClassType,
  VettingProvider,
} from '../../types';
import {
  applyForVetting,
  getBrand,
  getBrandFeedback,
  getCountries,
  getMnos,
  getMnoSuspensionStatus,
  getStockExchanges,
  getUsStates,
  getVerticalTypes,
  getVettingAppeals,
  getVettingProviders,
  getVettingProvidersByVettingClass,
  importVettingRecords,
  revetBrand,
  updateBrand,
} from '../../apis';
import {
  filterAppealCategoriesByVettingType,
  filterVettingAppealsByVettingClass,
  filterVettingClassOptionsByBrand,
  filterVettingProviderOptionsByBrand,
  findCmsRecordByKey,
  groupAppealCategoryIds,
  isBusinessContactEmailVisible,
} from '../../utils';
import {
  selectAppealCategories,
  selectDynamicContent,
  selectVettingSettings,
} from '../../../CSP-Form/selectors';
import { VettingType } from '../../../CSP-Form/constants';
import { ASSET_LINK_UPLOAD_ENABLED } from '@/constants';
import { BRANDS_DEFAULT_ENTRY } from '@/constants/paths';
import { Loader } from '@/shared_elements';
import { isValidInput, toastFlashMessage } from '@/utils';
import { EmptyString, SelectItem } from '@/utils/types';
import { BrandErrorCode } from '@/applications/CspPortal';
import { fieldValidation } from '@/utils/validator';
import { AssetUploadModal, Tab } from '@/components';
import { BrandAssetType } from '@/apis/core';

import '@/assets/styles/brand-view-module.scss';

interface BrandDetailsProps {
  brandUid?: string;
}

interface LocationState {
  goBackPage?: string;
}

interface VettingInfo {
  vettingClass: VettingClassType | EmptyString;
  evpUid: string;
  website: string;
  businessContactEmail: string;
  vettingId: string;
  vettingToken: string;
  errors: Record<string, string>;
}

interface VettingResult {
  success: boolean;
  vettingScore?: number;
  importError: boolean;
  customError: string;
}

enum ModalType {
  None = 'none',
  Edit = 'edit',
  Resubmit = 'resubmit',
  Vet = 'vet',
  Political = 'political',
  Status = 'status',
  RcsVetSuccess = 'rcsVetSuccess',
  RcsVetAppeal = 'rcsVetAppeal',
  BannerUpload = 'bannerUpload',
  LogoUpload = 'logoUpload',
}

const BrandDetails: FunctionComponent = () => {
  const { id: brandUidFromParams } = useParams<{ id: string }>();
  const brandUid = brandUidFromParams || '';
  const dynamicContent = useSelector(selectDynamicContent);
  const appealCategories = useSelector(selectAppealCategories);
  const vettingSettings = useSelector(selectVettingSettings);
  const history = useHistory();
  const location = useLocation<LocationState>();
  const goBackPage = location.state?.goBackPage ?? BRANDS_DEFAULT_ENTRY;
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState<BrandDetail>();
  const [feedback, setFeedback] = useState<BrandFeedback[]>([]);
  const [vettingAppeals, setVettingAppeals] = useState<VettingAppeal[]>([]);
  const [mnos, setMnos] = useState<Mno[]>([]);
  const [mnoSuspensionStatus, setMnoSuspensionStatus] = useState<
    Array<MnoSuspensionStatus>
  >([]);
  const [countries, setCountries] = useState<SelectItem[]>([]);
  const [usStates, setUsStates] = useState<SelectItem[]>([]);
  const [verticalTypes, setVerticalTypes] = useState<SelectItem[]>([]);
  const [stockExchanges, setStockExchanges] = useState<SelectItem[]>([]);
  const [vettingProviders, setVettingProviders] = useState<VettingProvider[]>(
    []
  );
  const [vettingProvidersByVettingClass, setVettingProvidersByVettingClass] =
    useState<Record<string, VettingProvider[]>>({});
  const [modalType, setModalType] = useState(ModalType.None);
  const [modalLoading, setModalLoading] = useState(false);
  const [editModalType, setEditModalType] = useState(EditModal.companyName);
  const [vettingModalType, setVettingModalType] = useState(
    VettingModal.newVetting
  );
  const [vettingInfo, setVettingInfo] = useState<VettingInfo>({
    vettingClass: '',
    evpUid: '',
    website: '',
    businessContactEmail: '',
    vettingId: '',
    vettingToken: '',
    errors: {},
  });
  const [vettingResult, setVettingResult] = useState<VettingResult>({
    success: false,
    vettingScore: undefined,
    importError: false,
    customError: '',
  });
  const [statusModalType, setStatusModalType] = useState(
    StatusModal.vettingResult
  );
  const vettingDetailsRef = useRef<BrandVettingDetailsBlockHandle>(null);
  const isSoleProp = brand?.entityType === 'SOLE_PROPRIETOR';
  const resubmitContent = useMemo(() => {
    if (isSoleProp) {
      return `Are you sure you want to resubmit?`;
    }
    return (
      <>
        Are you sure? <span>You will be charged $4.50</span>
        <br /> every time you resubmit a Brand
        <br /> for identity verification.
      </>
    );
  }, [isSoleProp]);

  const newVettingData = useMemo(
    () =>
      findCmsRecordByKey(
        dynamicContent,
        'csp-brand-vetting-modal#new-vetting'
      ) ?? '',
    [dynamicContent]
  );
  const importVettingData = useMemo(
    () =>
      findCmsRecordByKey(
        dynamicContent,
        'csp-brand-vetting-modal#import-vetting'
      ) ?? '',
    [dynamicContent]
  );
  const identityVettingAppeals = useMemo(
    () =>
      filterVettingAppealsByVettingClass(vettingAppeals, VettingType.Identity),
    [vettingAppeals]
  );
  const standardVettingAppeals = useMemo(
    () => [
      ...filterVettingAppealsByVettingClass(
        vettingAppeals,
        VettingType.Standard
      ),
      ...filterVettingAppealsByVettingClass(
        vettingAppeals,
        VettingType.Authplus
      ),
      ...filterVettingAppealsByVettingClass(vettingAppeals, VettingType.RCS),
    ],
    [vettingAppeals]
  );
  const identityAppealCategories = useMemo(
    () =>
      filterAppealCategoriesByVettingType(
        appealCategories,
        VettingType.Identity
      ),
    [appealCategories]
  );

  const assetAppealCategories = useMemo(
    () =>
      filterAppealCategoriesByVettingType(appealCategories, VettingType.Asset),
    [appealCategories]
  );
  const {
    enabledIdentityAppealCategoryIds,
    disabledIdentityAppealCategoryIds,
    enabledAssetAppealCategoryIds,
    disabledAssetAppealCategoryIds,
  } = useMemo(
    () => groupAppealCategoryIds(vettingSettings, brand),
    [vettingSettings, brand]
  );

  const suspendedStatus = useMemo(
    () =>
      mnos.map(
        (mno) =>
          ({
            networkId: mno.networkId,
            displayName: mno.displayName,
            suspended: !!mnoSuspensionStatus.find(
              ({ mnoId }) => mnoId === mno.networkId
            )?.suspended,
          } as MNOSuspendState)
      ),
    [mnos, mnoSuspensionStatus]
  );
  const vettingClassOptions = useMemo(
    () =>
      filterVettingClassOptionsByBrand(vettingProvidersByVettingClass, brand),
    [brand, vettingProvidersByVettingClass]
  );
  const vettingProviderOptions = useMemo(
    () =>
      filterVettingProviderOptionsByBrand(
        vettingInfo.vettingClass
          ? vettingProvidersByVettingClass[vettingInfo.vettingClass]
          : vettingProviders,
        vettingModalType === VettingModal.newVetting,
        brand
      ),
    [
      brand,
      vettingModalType,
      vettingProviders,
      vettingProvidersByVettingClass,
      vettingInfo.vettingClass,
    ]
  );
  const isBusinessContactEmailRequiredForVetting = useMemo(
    () =>
      isBusinessContactEmailVisible(brand?.entityType) &&
      !brand?.businessContactEmail,
    [brand]
  );
  const isWebsiteRequiredForVetting = useMemo(() => !brand?.website, [brand]);

  const handleGoBack = () => {
    history.push(goBackPage);
  };

  const handleBrandEdit = (type: string) => {
    setModalType(ModalType.Edit);
    setEditModalType(type as EditModal);
  };

  const { triggerBrandRefresh, updateBrandStatus } = useBrandContext();
  const handleBrandUpdate = async (data: Record<string, unknown>) => {
    setModalLoading(true);
    const response = await updateBrand(brandUid, data);
    if (response) {
      toastFlashMessage('Brand updated successfully', 'success');
      setBrand(response);
      setModalType(ModalType.None);
      if (response.status) {
        updateBrandStatus(response.status as BrandStatus);
      }
      triggerBrandRefresh();
    }
    setModalLoading(false);
  };

  const handleBrandReload = async () => {
    setLoading(true);
    const response = await getBrand(brandUid);
    if (response) {
      setBrand(response);
      if (response.status) {
        updateBrandStatus(response.status as BrandStatus);
      }
    }
    setLoading(false);
  };

  const handleFeedbackReload = async () => {
    const response = await getBrandFeedback(brandUid);
    setFeedback(response);
  };

  const handleVettingRecordsReload = async () => {
    vettingDetailsRef.current?.reloadVettingRecords();
  };

  const handleBrandResubmit = () => {
    setModalType(ModalType.Resubmit);
  };

  const handleBrandRevet = async (flag: boolean) => {
    if (flag) {
      setModalLoading(true);
      try {
        const response = await revetBrand(brandUid);
        if (response) {
          toastFlashMessage('Brand resubmitted successfully', 'success');
          setBrand(response);
          setModalType(ModalType.None);
          handleFeedbackReload();
        }
      } catch (error) {
        setModalType(ModalType.Status);
        setStatusModalType(StatusModal.vettingResult);
        setVettingResult({
          ...vettingResult,
          success: false,
          customError: (error as Error).message,
        });
      }
      setModalLoading(false);
    } else {
      setModalType(ModalType.None);
    }
  };

  const handleAppealCreate = (appeal: VettingAppeal) => {
    setVettingAppeals([...vettingAppeals, appeal]);
  };

  const handleAssetUpload = (type: string) => {
    if (type === 'logo' || type === 'banner') {
      if (!ASSET_LINK_UPLOAD_ENABLED) {
        history.push({
          pathname: `/brands/${brandUid}/upload/${type}`,
          state: { goBackPage },
        });
      } else {
        setModalType(
          type === 'logo' ? ModalType.LogoUpload : ModalType.BannerUpload
        );
      }
    }
  };

  const handleAssetEmulate = () => {
    history.push({
      pathname: `/brands/${brandUid}/emulator`,
      state: { goBackPage },
    });
  };

  const handleBrandVet = (configs: Record<string, unknown>) => {
    if (configs.modalType) {
      setModalType(ModalType.Vet);
      setVettingModalType(configs.modalType as VettingModal);
    }
  };

  const handleVettingInfoChange = (key: string, value: string) => {
    const newVettingInfo = {
      ...vettingInfo,
      evpUid: key === 'vettingClass' ? '' : vettingInfo.evpUid,
      vettingToken: key === 'evpUid' ? '' : vettingInfo.vettingToken,
      [key]: value,
      errors: {
        ...vettingInfo.errors,
        vettingToken: key === 'evpUid' ? '' : vettingInfo.errors.vettingToken,
        [key]: '',
      },
    };
    setVettingInfo(newVettingInfo);
  };

  const validateVettingInfo = (key: string, value?: string) => {
    const trimmedValue = value?.trim() || '';
    if (key === 'vettingId') {
      return trimmedValue
        ? isValidInput(trimmedValue)
          ? ''
          : 'Invalid input'
        : 'Please enter Vetting Identifier';
    } else if (key === 'vettingToken' && vettingInfo.evpUid === 'AEGIS') {
      return trimmedValue
        ? isValidInput(trimmedValue)
          ? ''
          : 'Invalid input'
        : 'Please enter Vetting Token';
    } else if (key === 'businessContactEmail') {
      const validationResult = fieldValidation({
        ...BrandErrorCode.businessContactEmailObj,
        fieldval: value,
      });
      if (validationResult !== 2) {
        return BrandErrorCode.businessContactEmail[validationResult];
      }
    } else if (key === 'website') {
      const validationResult = fieldValidation({
        ...BrandErrorCode.websiteRequiredObj,
        fieldval: value,
      });
      if (validationResult !== 2 && validationResult !== 5) {
        return BrandErrorCode.websiteRequired[validationResult];
      }
    }
    return '';
  };

  const handleVettingInfoValidate = (key: string, value: string) => {
    setVettingInfo({
      ...vettingInfo,
      errors: {
        ...vettingInfo.errors,
        [key]: validateVettingInfo(key, value),
      },
    });
  };

  const applyVet = async (data: Record<string, unknown>) => {
    setModalLoading(true);
    try {
      const record = await applyForVetting(brandUid, vettingInfo.evpUid, data);
      if (record) {
        setModalType(ModalType.Status);
        setStatusModalType(StatusModal.vettingResult);
        setVettingInfo({
          ...vettingInfo,
          vettingId: record.vettingId,
        });
        setVettingResult({
          ...vettingResult,
          success: Boolean(
            record.score && record.vettedDate && record.vettingId
          ),
          vettingScore: record.score || undefined,
        });
        handleVettingRecordsReload();
      }
    } catch (error) {
      setModalType(ModalType.Status);
      setStatusModalType(StatusModal.vettingResult);
      setVettingResult({
        ...vettingResult,
        success: false,
        customError: (error as Error).message,
      });
    }
    setModalLoading(false);
  };

  const applyRcsVet = async () => {
    setModalLoading(true);
    const isUpdateBrandNeeded =
      !!vettingInfo.businessContactEmail || !!vettingInfo.website;
    if (isUpdateBrandNeeded) {
      const brandDetail = await updateBrandForVetting();
      if (!brandDetail) {
        setModalLoading(false);
        return;
      }
      setBrand(brandDetail);
    }

    try {
      const record = await applyForVetting(brandUid, vettingInfo.evpUid, {
        vettingClass: vettingInfo.vettingClass,
      });
      if (record) {
        if (isUpdateBrandNeeded) {
          setModalType(ModalType.RcsVetSuccess);
        } else {
          toastFlashMessage('RCS vet successfully submitted', 'success');
          setModalType(ModalType.None);
        }
        handleVettingRecordsReload();
      }
    } catch (error) {
      setModalType(ModalType.Status);
      setStatusModalType(StatusModal.vettingResult);
      setVettingResult({
        ...vettingResult,
        success: false,
        customError: (error as Error).message,
      });
    } finally {
      setModalLoading(false);
    }
  };

  const updateBrandForVetting = async () => {
    const response = await updateBrand(brandUid, {
      ...(vettingInfo.website ? { website: vettingInfo.website } : {}),
      ...(vettingInfo.businessContactEmail
        ? { businessContactEmail: vettingInfo.businessContactEmail }
        : {}),
    });
    return response;
  };

  const handleExternalVet = async () => {
    if (vettingModalType === VettingModal.newVetting) {
      if (
        vettingInfo.vettingClass === 'POLITICAL' &&
        vettingInfo.evpUid === 'AEGIS'
      ) {
        setModalType(ModalType.Political);
      } else if (vettingInfo.vettingClass === 'RCS') {
        await applyRcsVet();
      } else {
        if (
          vettingInfo.vettingClass === 'AUTHPLUS' &&
          (isBusinessContactEmailRequiredForVetting ||
            isWebsiteRequiredForVetting)
        ) {
          if (isBusinessContactEmailRequiredForVetting) {
            const businessContactEmailError = validateVettingInfo(
              'businessContactEmail',
              vettingInfo.businessContactEmail
            );
            if (businessContactEmailError) {
              return;
            }
          }
          if (isWebsiteRequiredForVetting) {
            const websiteError = validateVettingInfo(
              'website',
              vettingInfo.website
            );
            if (websiteError) {
              return;
            }
          }

          const response = await updateBrandForVetting();
          if (response) {
            setBrand(response);
          } else {
            return;
          }
        }
        await applyVet({ vettingClass: vettingInfo.vettingClass });
      }
    } else {
      const vettingIdValidation = validateVettingInfo(
        'vettingId',
        vettingInfo.vettingId
      );
      const vettingTokenValidation = validateVettingInfo(
        'vettingToken',
        vettingInfo.vettingToken
      );
      if (vettingIdValidation || vettingTokenValidation) {
        setVettingInfo({
          ...vettingInfo,
          errors: {
            vettingId: vettingIdValidation,
            vettingToken: vettingTokenValidation,
          },
        });
      } else {
        setModalLoading(true);
        try {
          const record = await importVettingRecords(
            brandUid,
            vettingInfo.evpUid,
            vettingInfo.vettingId,
            vettingInfo.evpUid === 'AEGIS'
              ? { vettingToken: encodeURIComponent(vettingInfo.vettingToken) }
              : {}
          );
          if (record) {
            setModalType(ModalType.Status);
            setStatusModalType(StatusModal.vettingStatus);
            setVettingInfo({
              ...vettingInfo,
              vettingClass: record.vettingClass as VettingClassType,
            });
            setVettingResult({
              ...vettingResult,
              success: Boolean(
                record.score && record.vettedDate && record.vettingId
              ),
              vettingScore: record.score || undefined,
            });
            handleVettingRecordsReload();
          }
        } catch (error) {
          setModalType(ModalType.Status);
          setStatusModalType(StatusModal.vettingStatus);
          setVettingResult({
            ...vettingResult,
            success: false,
            importError: true,
          });
        }
        setModalLoading(false);
      }
    }
  };

  const handlePoliticalVet = async (data: Record<string, unknown>) => {
    await applyVet({
      vettingClass: vettingInfo.vettingClass,
      additionalRequestPayload: JSON.stringify(data),
    });
  };

  const handleModalClose = () => {
    setModalType(ModalType.None);
  };

  const deleteBrandGuard = (func: (...args: unknown[]) => unknown) => {
    return (...args: unknown[]) => {
      if (brand?.status !== BrandStatus.Deleted) {
        return func(...args);
      }
      toastFlashMessage(
        'Brand is deleted and the action cannot be applied.',
        'error'
      );
      return undefined;
    };
  };

  useEffect(() => {
    (async () => {
      const [
        brandPromise,
        feedbackPromise,
        vettingAppealsPromise,
        mnosPromise,
        mnoSuspensionStatusPromise,
        countriesPromise,
        usStatesPromise,
        verticalTypesPromise,
        stockExchangesPromise,
        vettingProvidersPromise,
        vettingProvidersByVettingClassPromise,
      ] = await Promise.allSettled([
        getBrand(brandUid),
        getBrandFeedback(brandUid),
        getVettingAppeals(brandUid),
        getMnos(),
        getMnoSuspensionStatus(brandUid),
        getCountries(),
        getUsStates(),
        getVerticalTypes(),
        getStockExchanges(),
        getVettingProviders(),
        getVettingProvidersByVettingClass(),
      ]);
      if (brandPromise.status === 'fulfilled') {
        setBrand(brandPromise.value);
        if (brandPromise.value?.status) {
          updateBrandStatus(brandPromise.value.status as BrandStatus);
        }
      }
      if (feedbackPromise.status === 'fulfilled') {
        setFeedback(feedbackPromise.value);
      }
      if (vettingAppealsPromise.status === 'fulfilled') {
        setVettingAppeals(vettingAppealsPromise.value);
      }
      if (mnosPromise.status === 'fulfilled') {
        setMnos(mnosPromise.value);
      }
      if (mnoSuspensionStatusPromise.status === 'fulfilled') {
        setMnoSuspensionStatus(mnoSuspensionStatusPromise.value);
      }
      if (countriesPromise.status === 'fulfilled') {
        setCountries(countriesPromise.value);
      }
      if (usStatesPromise.status === 'fulfilled') {
        setUsStates(usStatesPromise.value);
      }
      if (verticalTypesPromise.status === 'fulfilled') {
        setVerticalTypes(verticalTypesPromise.value);
      }
      if (stockExchangesPromise.status === 'fulfilled') {
        setStockExchanges(stockExchangesPromise.value);
      }
      if (vettingProvidersPromise.status === 'fulfilled') {
        setVettingProviders(vettingProvidersPromise.value);
      }
      if (vettingProvidersByVettingClassPromise.status === 'fulfilled') {
        setVettingProvidersByVettingClass(
          vettingProvidersByVettingClassPromise.value
        );
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (modalType === ModalType.None) {
      setModalLoading(false);
      setVettingInfo({
        vettingClass: '',
        evpUid: '',
        website: '',
        businessContactEmail: '',
        vettingId: '',
        vettingToken: '',
        errors: {},
      });
      setVettingResult({
        success: false,
        vettingScore: undefined,
        importError: false,
        customError: '',
      });
    }
  }, [modalType]);

  if (!loading && !brand) {
    return <BrandNotFound />;
  }

  return (
    <section className="brand-view-module" data-testid="brandDetails">
      {loading || !brand ? (
        <Loader />
      ) : (
        <Container
          maxWidth={false}
          style={{ padding: '0px' }}
          className="brand-view-container"
          data-testid="brandDetailsContainer"
        >
          <BrandDetailTopBlock
            backLink={goBackPage}
            handleGoBack={handleGoBack}
          />
          <BrandBasicDetails
            brandUid={brandUid}
            brand={brand}
            handleEditClick={handleBrandEdit}
            getBrandDetails={handleBrandReload}
            actionGuard={deleteBrandGuard}
          />
          <BrandIdentityStatus
            brand={brand}
            feedbackInfo={feedback}
            appeals={identityVettingAppeals}
            appealCategories={identityAppealCategories}
            validAppealCategoryIds={enabledIdentityAppealCategoryIds}
            disabledAppealCategoryIds={disabledIdentityAppealCategoryIds}
            onAppealCreate={handleAppealCreate}
            onResubmitClick={handleBrandResubmit}
            actionGuard={deleteBrandGuard}
          />

          <Grid
            container
            spacing={0}
            className="brand-details more"
            justifyContent="center"
          >
            <BrandCompanyContactBlock
              brand={brand}
              handleEditClick={handleBrandEdit}
              actionGuard={deleteBrandGuard}
            />
            <BrandVettingDetailsBlock
              ref={vettingDetailsRef}
              brand={brand}
              appeals={standardVettingAppeals}
              feedbackInfo={feedback}
              onAppealCreate={handleAppealCreate}
              handleState={handleBrandVet}
              actionGuard={deleteBrandGuard}
            />
            {!isSoleProp && (
              <BrandAssets
                onOpenUploader={handleAssetUpload}
                onOpenEmulator={handleAssetEmulate}
                actionGuard={deleteBrandGuard}
                appealCategories={assetAppealCategories}
                enabledCategoryIds={enabledAssetAppealCategoryIds}
                onAppealCreated={handleAppealCreate}
              />
            )}
            <BrandSuspendedStatusBlock mnos={suspendedStatus} />
            <BrandRegisteredCampaigns
              brandUid={brandUid}
              brandStatus={brand.status}
            />
            <BrandEvents brandUid={brandUid} />
          </Grid>
        </Container>
      )}

      <ConfirmationDialog
        open={modalType === ModalType.Resubmit}
        loader={modalLoading}
        submitText="Proceed"
        content={resubmitContent}
        handleClick={handleBrandRevet}
      />
      {brand && (
        <>
          <BrandEditModal
            open={modalType === ModalType.Edit}
            loader={modalLoading}
            type={editModalType}
            brandInfo={brand}
            stockExchanges={stockExchanges}
            verticalTypes={verticalTypes}
            countries={countries}
            usStates={usStates}
            handleClose={handleModalClose}
            handleSubmitEdit={handleBrandUpdate}
            onTypeChange={handleBrandEdit}
          />
          <BrandVettingModalV2
            open={modalType === ModalType.Vet}
            vettingLoader={modalLoading}
            modalType={vettingModalType}
            newVettingData={newVettingData}
            importVettingData={importVettingData}
            vettingClasses={vettingClassOptions}
            vettingProviders={vettingProviderOptions}
            vettingClass={vettingInfo.vettingClass}
            extVettingProviderId={vettingInfo.evpUid}
            vettingId={vettingInfo.vettingId}
            vettingToken={vettingInfo.vettingToken}
            error={vettingInfo.errors}
            handleChange={handleVettingInfoChange}
            handleError={handleVettingInfoValidate}
            handleClose={handleModalClose}
            submitVettingRequest={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              handleExternalVet();
            }}
            isBusinessContactEmailRequired={
              isBusinessContactEmailRequiredForVetting
            }
            businessContactEmail={vettingInfo.businessContactEmail || ''}
            isWebsiteRequired={isWebsiteRequiredForVetting}
            website={vettingInfo.website}
          />
          <PoliticalVettingModal
            open={modalType === ModalType.Political}
            brand={brand}
            onClose={handleModalClose}
            onSubmit={handlePoliticalVet}
          />
          <VettingStatusModal
            open={modalType === ModalType.Status}
            modalType={statusModalType}
            brandName={brand.displayName}
            vettingProviders={vettingProviders}
            vettingClass={vettingInfo.vettingClass}
            extVettingProviderId={vettingInfo.evpUid}
            vettingId={vettingInfo.vettingId}
            vettingToken={vettingInfo.vettingToken}
            vettingConfirmed={vettingResult.success}
            vettingScore={vettingResult.vettingScore}
            importVettingError={vettingResult.importError}
            applyError={!!vettingResult.customError}
            applyErrorMsg={vettingResult.customError}
            handleClose={handleModalClose}
          />
          <RcsVetSuccessModal
            open={modalType === ModalType.RcsVetSuccess}
            onClose={handleModalClose}
          />
          {ASSET_LINK_UPLOAD_ENABLED && (
            <AssetUploadModal
              assetType={
                modalType === ModalType.LogoUpload
                  ? BrandAssetType.Logo
                  : BrandAssetType.Banner
              }
              brandUid={brandUid}
              onClose={() => {
                handleModalClose();
              }}
              onUpdate={() => {
                triggerBrandRefresh();
              }}
              onSubmit={handleModalClose}
              dialogTitle={
                modalType === ModalType.LogoUpload
                  ? 'Brand Logo Upload'
                  : 'Brand Banner Upload'
              }
              panelDescription={{
                [Tab.Image]: (
                  <>
                    Upload an image of the brand{' '}
                    {modalType === ModalType.LogoUpload ? 'logo' : 'banner'}{' '}
                  </>
                ),
                [Tab.Link]: (
                  <>
                    Upload an image of the brand{' '}
                    {modalType === ModalType.LogoUpload ? 'logo' : 'banner'}.
                    <br />
                    Image must be{' '}
                    {modalType === ModalType.LogoUpload
                      ? '224x224'
                      : '1440x448'}
                    px.
                  </>
                ),
              }}
              open={
                modalType === ModalType.LogoUpload ||
                modalType === ModalType.BannerUpload
              }
            />
          )}
        </>
      )}
    </section>
  );
};

const BrandDetailsWithProvider: FunctionComponent<BrandDetailsProps> = ({
  brandUid,
}) => {
  return (
    <BrandProvider>
      <BrandDetails brandUid={brandUid} />
    </BrandProvider>
  );
};

export default BrandDetailsWithProvider;
